import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * AdminEmail — Full-screen email broadcast page.
 * Renders as a fixed overlay covering the entire viewport.
 * The send button is always visible in a pinned bottom bar.
 */

const AUDIENCES = [
    { id: 'single',   label: 'Single Address', icon: '✉️' },
    { id: 'waitlist', label: 'Waitlist',        icon: '📋' },
    { id: 'users',    label: 'All Users',       icon: '👥' },
    { id: 'all',      label: 'Everyone',        icon: '🌐' },
    { id: 'smart',    label: 'Smart Filter',    icon: '🎯' },
];

const AdminEmail = ({ onClose }) => {
    const [audience,       setAudience]       = useState('single');
    const [to,             setTo]             = useState('');
    const [subject,        setSubject]        = useState('');
    const [body,           setBody]           = useState('');
    const [sending,        setSending]        = useState(false);
    const [result,         setResult]         = useState(null);
    const [counts,         setCounts]         = useState(null);
    const [previewHtml,    setPreviewHtml]    = useState('');
    const [previewOpen,    setPreviewOpen]    = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    // Smart filter
    const [presets,        setPresets]        = useState([]);
    const [selectedPreset, setSelectedPreset] = useState('');
    const [presetResult,   setPresetResult]   = useState(null);
    const [presetLoading,  setPresetLoading]  = useState(false);
    const [smartEmails,    setSmartEmails]    = useState(null);
    // Search autocomplete
    const [searchQ,        setSearchQ]        = useState('');
    const [searchResults,  setSearchResults]  = useState([]);
    const [searchOpen,     setSearchOpen]     = useState(false);
    const [searchLoading,  setSearchLoading]  = useState(false);
    const searchRef = useRef(null);

    useEffect(() => {
        fetch('/api/admin/email/audience-counts', { credentials: 'include' })
            .then(r => r.json()).then(setCounts).catch(() => {});
        fetch('/api/admin/email/presets', { credentials: 'include' })
            .then(r => r.json()).then(d => setPresets(d.presets || [])).catch(() => {});
    }, []);

    // Debounced email search
    useEffect(() => {
        if (searchQ.length < 1) { setSearchResults([]); setSearchOpen(false); return; }
        const t = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const r = await fetch(`/api/admin/email/search?q=${encodeURIComponent(searchQ)}`, { credentials: 'include' });
                const data = await r.json();
                setSearchResults(data.results || []);
                setSearchOpen((data.results || []).length > 0);
            } catch (_) {}
            setSearchLoading(false);
        }, 180);
        return () => clearTimeout(t);
    }, [searchQ]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = e => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const pickEmail = useCallback((email) => {
        setTo(email);
        setSearchQ(email);
        setSearchOpen(false);
    }, []);

    const handleFindPreset = async () => {
        if (!selectedPreset) return;
        setPresetLoading(true);
        setPresetResult(null);
        setSmartEmails(null);
        try {
            const r = await fetch(`/api/admin/email/preset-audience?preset=${selectedPreset}`, { credentials: 'include' });
            const data = await r.json();
            setPresetResult(r.ok ? data : { error: data.error });
        } catch (err) { setPresetResult({ error: err.message }); }
        setPresetLoading(false);
    };

    const handleApprovePreset = () => {
        if (presetResult?.emails?.length) setSmartEmails(presetResult.emails);
    };

    const recipientCount = () => {
        if (audience === 'single') return to ? 1 : 0;
        if (audience === 'smart')  return smartEmails ? smartEmails.length : (presetResult?.count ?? 0);
        return counts?.[audience] ?? '…';
    };

    const canSend = subject.trim() && body.trim() && (
        (audience === 'single'  && to.trim()) ||
        (audience === 'smart'   && smartEmails?.length) ||
        (audience !== 'single'  && audience !== 'smart')
    );

    const handlePreview = async () => {
        if (!subject.trim() || !body.trim()) return;
        setPreviewLoading(true);
        try {
            const r = await fetch('/api/admin/email/preview', {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ subject, body }),
            });
            const data = await r.json();
            setPreviewHtml(data.html || '');
            setPreviewOpen(true);
        } catch (_) {}
        setPreviewLoading(false);
    };

    const handleSend = async () => {
        if (!canSend) return;
        const n = recipientCount();
        if (!window.confirm(`Send to ${n} recipient${n !== 1 ? 's' : ''}?\n\nSubject: "${subject}"`)) return;
        setSending(true);
        setResult(null);
        // For smart sends, pass the resolved email list directly
        const body_payload = audience === 'smart'
            ? { audience: 'single_list', emails: smartEmails, subject, body }
            : { audience, to, subject, body };
        try {
            const r = await fetch('/api/admin/email/send', {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body_payload),
            });
            const data = await r.json();
            setResult(r.ok ? { ...data, ok: true } : { ok: false, error: data.error });
        } catch (err) {
            setResult({ ok: false, error: err.message });
        }
        setSending(false);
    };

    const inp = {
        style: {
            width: '100%', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
            padding: '10px 14px', color: '#fff', fontSize: 13,
            fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
        }
    };

    return (
        /* ── Full-screen fixed overlay ── */
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: '#0a0a12',
            display: 'flex', flexDirection: 'column',
        }}>
            {/* ── Top bar ── */}
            <div style={{
                padding: '16px 32px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                background: '#0d0d1a',
                display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
            }}>
                {/* Back button */}
                {onClose && (
                    <button
                        onClick={onClose}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: 'rgba(255,255,255,0.04)',
                            color: '#9ca3af', fontSize: 12, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.1em',
                            transition: 'all 0.15s',
                            marginRight: 8,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#9ca3af'; }}
                        id="email-back-btn"
                    >
                        ← Dashboard
                    </button>
                )}
                <span style={{ fontSize: 18, fontWeight: 900, color: '#fff', letterSpacing: '0.05em' }}>
                    ✉️ Email Broadcast
                </span>
                <span style={{ fontSize: 12, color: '#4b5563' }}>
                    Sending from <span style={{ color: '#3b82f6', fontFamily: 'monospace' }}>noreply@minutewinit.com</span>
                </span>
                <div style={{ flex: 1 }} />
                {/* Audience counts */}
                {counts && (
                    <div style={{ display: 'flex', gap: 12 }}>
                        {[
                            { label: 'Users',    val: counts.users,    color: '#3b82f6' },
                            { label: 'Waitlist', val: counts.waitlist, color: '#10b981' },
                            { label: 'Unique',   val: counts.all,      color: '#a855f7' },
                        ].map(({ label, val, color }) => (
                            <div key={label} style={{
                                padding: '6px 14px', borderRadius: 8,
                                border: '1px solid rgba(255,255,255,0.06)',
                                background: 'rgba(255,255,255,0.02)', textAlign: 'center',
                            }}>
                                <div style={{ fontSize: 15, fontWeight: 900, color, fontFamily: 'monospace' }}>{val}</div>
                                <div style={{ fontSize: 9, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.15em' }}>{label}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Audience pills ── */}
            <div style={{
                padding: '14px 32px', display: 'flex', gap: 10, flexShrink: 0,
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                background: '#0b0b18',
            }}>
                {AUDIENCES.map(a => {
                    const cnt = a.id === 'single' ? (to ? 1 : null) : counts?.[a.id];
                    const active = audience === a.id;
                    return (
                        <button key={a.id} onClick={() => { setAudience(a.id); setResult(null); }}
                            id={`audience-${a.id}`}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 18px', borderRadius: 8, cursor: 'pointer',
                                border: active ? '1px solid rgba(59,130,246,0.6)' : '1px solid rgba(255,255,255,0.1)',
                                background: active ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.02)',
                                color: active ? '#fff' : '#6b7280',
                                fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                                letterSpacing: '0.1em', transition: 'all 0.15s',
                            }}>
                            <span>{a.icon}</span>
                            <span>{a.label}</span>
                            {cnt != null && (
                                <span style={{
                                    padding: '1px 7px', borderRadius: 4, fontSize: 11, fontWeight: 900,
                                    background: active ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.06)',
                                    color: active ? '#93c5fd' : '#6b7280',
                                }}>{cnt}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* ── Main content — fills remaining height ── */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

                {/* Left: compose form */}
                <div style={{
                    flex: 1, padding: '24px 32px',
                    display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto',
                }}>
                    {/* To field with search autocomplete */}
                    {audience === 'single' && (
                        <div ref={searchRef} style={{ position: 'relative' }}>
                            <label style={{ display: 'block', fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>
                                Recipient Email
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={searchQ}
                                    onChange={e => { setSearchQ(e.target.value); setTo(e.target.value); }}
                                    onFocus={() => searchQ.length >= 1 && searchResults.length > 0 && setSearchOpen(true)}
                                    placeholder="Type to search users & waitlist…"
                                    id="email-to"
                                    autoComplete="off"
                                    {...inp}
                                    style={{ ...inp.style, paddingRight: 36 }}
                                />
                                {/* Search status icon */}
                                <span style={{
                                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                    fontSize: 14, color: '#4b5563',
                                }}>
                                    {searchLoading ? '⏳' : searchQ.length >= 1 ? '🔍' : '✉️'}
                                </span>
                            </div>

                            {/* Dropdown results */}
                            {searchOpen && searchResults.length > 0 && (
                                <div style={{
                                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                    background: '#13131f', border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: 8, marginTop: 4, overflow: 'hidden',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                                }}>
                                    {searchResults.map((r, i) => (
                                        <button
                                            key={r.email}
                                            onClick={() => pickEmail(r.email)}
                                            style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                width: '100%', padding: '10px 14px', textAlign: 'left',
                                                background: 'transparent', border: 'none',
                                                borderBottom: i < searchResults.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                                                cursor: 'pointer', transition: 'background 0.1s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <span style={{ fontSize: 13, color: '#e5e7eb', fontFamily: 'monospace' }}>
                                                {r.email}
                                            </span>
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                                                letterSpacing: '0.1em', padding: '2px 8px', borderRadius: 4,
                                                background: r.source === 'user' ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)',
                                                color: r.source === 'user' ? '#60a5fa' : '#34d399',
                                            }}>
                                                {r.source === 'user' ? (r.name || 'user') : 'waitlist'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Smart Filter */}
                    {audience === 'smart' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {/* Category groups */}
                            {['Re-engagement','Activity','Financial','New Users','Waitlist'].map(cat => {
                                const catPresets = presets.filter(p => p.category === cat);
                                if (!catPresets.length) return null;
                                return (
                                    <div key={cat}>
                                        <p style={{ fontSize: 9, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: 6 }}>{cat}</p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {catPresets.map(p => (
                                                <button key={p.id} onClick={() => { setSelectedPreset(p.id); setPresetResult(null); setSmartEmails(null); }}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                                                        border: selectedPreset === p.id ? '1px solid rgba(168,85,247,0.6)' : '1px solid rgba(255,255,255,0.08)',
                                                        background: selectedPreset === p.id ? 'rgba(168,85,247,0.12)' : 'rgba(255,255,255,0.02)',
                                                        color: selectedPreset === p.id ? '#d8b4fe' : '#6b7280',
                                                        transition: 'all 0.12s',
                                                    }}>
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Selected preset description + Find button */}
                            {selectedPreset && (() => {
                                const p = presets.find(x => x.id === selectedPreset);
                                return p ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12,
                                        padding: '10px 14px', borderRadius: 8,
                                        background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)' }}>
                                        <span style={{ flex: 1, fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>{p.description}</span>
                                        <button onClick={handleFindPreset} disabled={presetLoading}
                                            id="preset-find-btn"
                                            style={{
                                                padding: '8px 20px', borderRadius: 8, cursor: presetLoading ? 'wait' : 'pointer',
                                                border: 'none', background: 'linear-gradient(135deg,#7c3aed,#a855f7)',
                                                color: '#fff', fontSize: 12, fontWeight: 900,
                                                textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0,
                                                opacity: presetLoading ? 0.6 : 1,
                                            }}>
                                            {presetLoading ? '⏳ Finding…' : '🔎 Find Recipients'}
                                        </button>
                                    </div>
                                ) : null;
                            })()}

                            {/* Preset result / approval panel */}
                            {presetResult && !presetResult.error && !smartEmails && (
                                <div style={{
                                    padding: '16px', borderRadius: 10,
                                    border: '1px solid rgba(168,85,247,0.3)',
                                    background: 'rgba(168,85,247,0.05)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                        <span style={{ fontSize: 13, fontWeight: 900, color: '#d8b4fe' }}>
                                            Found {presetResult.count} recipient{presetResult.count !== 1 ? 's' : ''}
                                        </span>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button onClick={() => { setPresetResult(null); }}
                                                style={{ padding: '6px 14px', borderRadius: 6, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#6b7280', fontSize: 11, fontWeight: 700 }}>
                                                ✕ Cancel
                                            </button>
                                            <button onClick={handleApprovePreset} disabled={!presetResult.count}
                                                id="preset-approve-btn"
                                                style={{ padding: '6px 18px', borderRadius: 6, cursor: presetResult.count ? 'pointer' : 'not-allowed', border: 'none', background: presetResult.count ? 'rgba(16,185,129,0.8)' : 'rgba(16,185,129,0.2)', color: '#fff', fontSize: 11, fontWeight: 900, letterSpacing: '0.05em' }}>
                                                ✓ Approve List
                                            </button>
                                        </div>
                                    </div>
                                    {/* Preview first 8 */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {presetResult.preview.map(p => (
                                            <div key={p.email} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{p.email}</span>
                                                {p.name && <span style={{ fontSize: 10, color: '#4b5563' }}>{p.name}</span>}
                                            </div>
                                        ))}
                                        {presetResult.count > 8 && (
                                            <p style={{ fontSize: 10, color: '#4b5563', marginTop: 4 }}>+ {presetResult.count - 8} more…</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Approved — ready to send */}
                            {smartEmails && (
                                <div style={{
                                    padding: '12px 16px', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.05)',
                                }}>
                                    <span style={{ fontSize: 13, fontWeight: 900, color: '#10b981' }}>
                                        ✓ {smartEmails.length} recipients approved
                                    </span>
                                    <button onClick={() => { setSmartEmails(null); setPresetResult(null); }}
                                        style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#6b7280', fontSize: 11 }}>
                                        Change
                                    </button>
                                </div>
                            )}

                            {presetResult?.error && (
                                <p style={{ fontSize: 12, color: '#ef4444', fontFamily: 'monospace' }}>❌ {presetResult.error}</p>
                            )}
                        </div>
                    )}

                    {/* Subject */}
                    <div>
                        <label style={{ display: 'block', fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>
                            Subject
                        </label>
                        <input type="text" value={subject} onChange={e => { setSubject(e.target.value); setResult(null); }}
                            placeholder="e.g. MinuteMarket is now live!" id="email-subject" {...inp} />
                    </div>

                    {/* Body — grows to fill space */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        <label style={{ display: 'block', fontSize: 10, color: '#6b7280', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 6 }}>
                            Message Body
                        </label>
                        <textarea value={body} onChange={e => { setBody(e.target.value); setResult(null); }}
                            placeholder={"Write your message here...\n\nBlank lines become new paragraphs."}
                            id="email-body"
                            style={{ ...inp.style, flex: 1, resize: 'none', minHeight: 0, fontFamily: 'monospace', lineHeight: 1.6 }}
                        />
                        <p style={{ fontSize: 10, color: '#374151', marginTop: 6 }}>
                            Plain text only — auto-wrapped in branded MinuteMarket template.
                        </p>
                    </div>

                    {/* Result panel */}
                    {result && (
                        <div style={{
                            padding: '16px 20px', borderRadius: 10, flexShrink: 0,
                            border: `1px solid ${result.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
                            background: result.ok ? 'rgba(16,185,129,0.05)' : 'rgba(239,68,68,0.05)',
                        }}>
                            {result.ok ? (
                                <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
                                    <span style={{ fontSize: 14, fontWeight: 900, color: '#10b981' }}>✅ Broadcast complete</span>
                                    {[
                                        { label: 'Sent',   val: result.sent,   color: '#10b981' },
                                        { label: 'Failed', val: result.failed, color: '#ef4444' },
                                        { label: 'Total',  val: result.total,  color: '#9ca3af' },
                                    ].map(({ label, val, color }) => (
                                        <div key={label} style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: 22, fontWeight: 900, color }}>{val}</div>
                                            <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.15em' }}>{label}</div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span style={{ color: '#ef4444', fontFamily: 'monospace', fontSize: 13 }}>❌ {result.error}</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Right: tips or preview */}
                <div style={{
                    width: 320, borderLeft: '1px solid rgba(255,255,255,0.06)',
                    background: '#0b0b18', overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}>
                    {previewOpen && previewHtml ? (
                        <>
                            <div style={{
                                padding: '10px 16px', display: 'flex', alignItems: 'center',
                                justifyContent: 'space-between', flexShrink: 0,
                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                            }}>
                                <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
                                    Email Preview
                                </span>
                                <button onClick={() => setPreviewOpen(false)}
                                    style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14 }}>✕</button>
                            </div>
                            <iframe title="email-preview" srcDoc={previewHtml}
                                style={{ flex: 1, border: 'none', background: '#0a0a12' }} />
                        </>
                    ) : (
                        <div style={{ padding: '24px 20px' }}>
                            <p style={{ fontSize: 10, color: '#4b5563', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 16 }}>Tips</p>
                            {[
                                'Free tier: 3,000/month · 100/day',
                                'Batch sends are rate-limited automatically',
                                'Use Preview to see branded email before sending',
                                'Blank lines in body = new paragraphs',
                                'Users + Waitlist are deduplicated on Everyone',
                            ].map((t, i) => (
                                <p key={i} style={{ fontSize: 12, color: '#4b5563', marginBottom: 10, lineHeight: 1.6 }}>• {t}</p>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── PINNED bottom action bar ── */}
            <div style={{
                padding: '16px 32px',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                background: '#0d0d1a',
                display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
            }}>
                <button onClick={handlePreview}
                    disabled={!subject.trim() || !body.trim() || previewLoading}
                    id="email-preview-btn"
                    style={{
                        padding: '10px 22px', borderRadius: 8, cursor: 'pointer',
                        border: '1px solid rgba(255,255,255,0.15)',
                        background: 'transparent', color: '#9ca3af',
                        fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em',
                        opacity: (!subject.trim() || !body.trim()) ? 0.3 : 1,
                    }}>
                    {previewLoading ? '⏳ Loading…' : '👁 Preview'}
                </button>

                <div style={{ flex: 1 }} />

                <span style={{ fontSize: 12, color: '#4b5563', fontFamily: 'monospace' }}>
                    {recipientCount()} recipient{recipientCount() !== 1 ? 's' : ''}
                </span>

                <button onClick={handleSend}
                    disabled={sending || !canSend}
                    id="email-send-btn"
                    style={{
                        padding: '12px 40px', borderRadius: 8, cursor: canSend && !sending ? 'pointer' : 'not-allowed',
                        border: 'none',
                        background: canSend && !sending
                            ? 'linear-gradient(135deg, #2563eb, #3b82f6)'
                            : 'rgba(59,130,246,0.2)',
                        color: '#fff', fontSize: 13, fontWeight: 900,
                        textTransform: 'uppercase', letterSpacing: '0.15em',
                        boxShadow: canSend && !sending ? '0 0 24px rgba(59,130,246,0.3)' : 'none',
                        transition: 'all 0.15s',
                        opacity: !canSend ? 0.4 : 1,
                        display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                    {sending ? (
                        <>
                            <span style={{
                                width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)',
                                borderTop: '2px solid #fff', borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite', display: 'inline-block',
                            }} />
                            Sending…
                        </>
                    ) : '🚀 Send'}
                </button>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default AdminEmail;
